import React from 'react';
import { SectionHeading, checkCls } from './ui.jsx';
import { ALL_DAYS } from './constants.js';

export default function AvailabilityGridSection({
  availableDays, setAvailableDays, availabilityGrid, setAvailabilityGrid, timeSlots,
}) {
  const cell = (day, slot) => `${day}_${slot.key}`;

  const toggleSlot = (day, slot) =>
    setAvailabilityGrid((prev) => ({ ...prev, [cell(day, slot)]: !prev[cell(day, slot)] }));

  const toggleDay = (day) => {
    const allChecked = timeSlots.every((s) => availabilityGrid[cell(day, s)]);
    const next = { ...availabilityGrid };
    for (const s of timeSlots) next[cell(day, s)] = !allChecked;
    setAvailabilityGrid(next);
  };

  const setAll = (predicate) => {
    const next = { ...availabilityGrid };
    for (const d of availableDays) {
      for (const s of timeSlots) next[cell(d, s)] = predicate(s);
    }
    setAvailabilityGrid(next);
  };

  const remainingDays = ALL_DAYS.filter((d) => !availableDays.includes(d));

  return (
    <div className="lg:col-span-6 space-y-4">
      <SectionHeading title="Set availability" />

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-1.5 p-2 bg-[#140b22] border border-white/15 rounded min-h-[42px]">
          {availableDays.map((day) => (
            <span
              key={day}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-white/10 text-white text-xs font-semibold"
            >
              {day}
              <button
                type="button"
                onClick={() => setAvailableDays(availableDays.filter((d) => d !== day))}
                className="hover:text-rose-400 cursor-pointer font-bold ml-1"
                aria-label={`Remove ${day}`}
              >
                ×
              </button>
            </span>
          ))}
          {remainingDays.length > 0 && (
            <select
              value=""
              onChange={(e) => e.target.value && setAvailableDays([...availableDays, e.target.value])}
              className="text-xs text-white/70 bg-transparent outline-none cursor-pointer border border-dashed border-white/20 px-2 py-1 rounded hover:border-civix-cyan/60"
            >
              <option value="">+ Add Day</option>
              {remainingDays.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          )}
        </div>

        <div className="flex items-center gap-2 text-[11px]">
          <button type="button" onClick={() => setAll(() => true)} className="text-civix-purpleSoft hover:underline font-semibold">
            Check All Slots
          </button>
          <span className="text-white/20">|</span>
          <button
            type="button"
            onClick={() => setAll((s) => s.startMinutes >= 14 * 60 && s.startMinutes < 20 * 60)}
            className="text-civix-purpleSoft hover:underline font-semibold"
          >
            Check 2 PM - 8 PM
          </button>
          <span className="text-white/20">|</span>
          <button type="button" onClick={() => setAll(() => false)} className="text-white/50 hover:underline">
            Clear All
          </button>
        </div>
      </div>

      <div className="border border-white/10 rounded overflow-hidden max-h-[500px] overflow-y-auto">
        <table className="w-full text-xs text-left border-collapse">
          <thead className="bg-[#1a0f2e] text-white/80 sticky top-0 z-10 border-b border-white/10">
            <tr>
              <th className="py-2 px-3 font-semibold w-36">Time Slot</th>
              {availableDays.map((day) => (
                <th
                  key={day}
                  onClick={() => toggleDay(day)}
                  className="py-2 px-2 text-center font-semibold hover:bg-white/10 cursor-pointer select-none"
                  title={`Click to toggle all slots for ${day}`}
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 bg-[#140b22]">
            {timeSlots.map((slot) => (
              <tr key={slot.key} className="hover:bg-white/5 transition">
                <td className="py-2 px-3 font-medium text-white/70 whitespace-nowrap bg-white/[0.02]">
                  {slot.label}
                </td>
                {availableDays.map((day) => (
                  <td key={cell(day, slot)} className="py-2 px-2 text-center">
                    <input
                      type="checkbox"
                      checked={Boolean(availabilityGrid[cell(day, slot)])}
                      onChange={() => toggleSlot(day, slot)}
                      className={`${checkCls} cursor-pointer w-4 h-4`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
